import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';

export interface FlightAnalysisResult {
  id: string;
  s3_key: string;
  file_type: string;
  overall_verdict: 'pass' | 'warning' | 'fail' | 'na';
  agent_interpretation: string | null;
  analysis_completed_at: string | null;
  created_at: string;
}

export interface FlightAnalysisResultDetail extends FlightAnalysisResult {
  check_results: Record<string, unknown> | null;
  agent_model: string | null;
}

/** Check result structure from engine startup analysis */
export interface EngineStartupCheck {
  check_id: string;
  name: string;
  verdict: 'pass' | 'warning' | 'fail';
  value: number | null;
  threshold: string;
  details: string;
}

export interface EngineStartupSuccessEvent {
  overall_verdict: 'pass' | 'warning' | 'fail';
  checks: EngineStartupCheck[];
}

export interface EngineStartupErrorEvent {
  error: string;
  signal_length?: number;
}

export type EngineStartupEvent = EngineStartupSuccessEvent | EngineStartupErrorEvent;

export function isStartupError(event: EngineStartupEvent): event is EngineStartupErrorEvent {
  return 'error' in event;
}

/** The queried shape: the badge fields plus the column the map is keyed on. */
interface AnalysisResultRow extends FlightAnalysisResult {
  object_id: string | null;
}

/**
 * Light query — fetch verdict + interpretation for a set of `fleet.file_objects`
 * ids. Used by FileGrid for badge display. Does NOT include check_results.
 *
 * Keyed on `object_id`, never on a path (#1326). This used to take the grid's
 * display keys and query `s3_key`, which holds a *storage* key: the grid strips
 * the org prefix that every stored key carries, so the two sides could not
 * match and the badge never rendered for any file in prod (628/628 rows measured
 * `organizations/…`-prefixed). #1280's uuid blob keys make any path↔key
 * resemblance permanently impossible, so the join has to be on identity.
 *
 * Entries with no `object_id` (the legacy flights arm) simply get no badge —
 * they are not in the id list and the caller's lookup misses, which is the same
 * "no verdict" state as an unanalyzed file.
 */
export function useFlightAnalysisResults(objectIds: string[]) {
  const [data, setData] = useState<Map<string, FlightAnalysisResult>>(new Map());
  const [loading, setLoading] = useState(false);

  // Copy before sorting — `objectIds` is the caller's memoized array and
  // sorting in place would reorder what it renders from.
  const idsJson = useMemo(() => JSON.stringify([...objectIds].sort()), [objectIds]);

  useEffect(() => {
    if (!objectIds.length) {
      setData(new Map());
      return;
    }

    let cancelled = false;

    const fetchResults = async () => {
      setLoading(true);
      const { data: rows, error } = await supabase
        .schema('fleet')
        .from('flight_analysis_results')
        .select(
          'id, s3_key, object_id, file_type, overall_verdict, agent_interpretation, analysis_completed_at, created_at'
        )
        .in('object_id', objectIds)
        .order('created_at', { ascending: false });

      if (cancelled) return;

      if (!error && rows) {
        const map = new Map<string, FlightAnalysisResult>();
        for (const row of rows as AnalysisResultRow[]) {
          // Ordered newest-first, so the first row seen for an object wins.
          if (row.object_id && !map.has(row.object_id)) {
            map.set(row.object_id, row);
          }
        }
        setData(map);
      }
      setLoading(false);
    };

    fetchResults();

    return () => {
      cancelled = true;
    };
  }, [idsJson]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading };
}

/**
 * Detail query — fetch full results including check_results for a single file.
 * Used by the flight-details dialog to show automated check details.
 *
 * Keyed on `object_id` (STD-029 D-8, closed here). This was the **last**
 * `s3_key` join in the product — R-14's one grandfathered reader, re-keyed on
 * touch. It worked, unlike its plural sibling, only because a flight row's
 * `s3_key` and the analysis row's `s3_key` were both raw storage keys; #1280's
 * uuid blob keys end that resemblance for all new data, so the join was a bet
 * on legacy rows continuing to look alike.
 *
 * The `s3_key` arm is **gone rather than kept as a fallback**, and that is a
 * deliberate call:
 *  - R-9 says an entry with no registry row has its id-keyed affordances hide.
 *    A verdict is one of them — that is the rule the grid badge already follows
 *    since #1326, and the two surfaces must not disagree about the same file:
 *    "no badge in the grid, full analysis in the dialog" is worse than either.
 *  - `flight_analysis_results.object_id` is fully backfilled (628/628, #1240),
 *    so every analysis row that exists is reachable by id.
 *  - A retained key join would be a new entry on the B-7 ratchet's allowlist,
 *    which only ever shrinks.
 *
 * The population this could change behaviour for is narrow and measurable: a
 * flight whose own `object_id IS NULL` but whose analysis row nonetheless
 * carries a matching `s3_key`. The audit SQL is in the PR/build report.
 */
export function useFlightAnalysisResult(objectId: string | null) {
  const [data, setData] = useState<FlightAnalysisResultDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!objectId) {
      setData(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchResult = async () => {
      setLoading(true);
      const { data: rows, error } = await supabase
        .schema('fleet')
        .from('flight_analysis_results')
        .select(
          'id, s3_key, file_type, overall_verdict, check_results, agent_interpretation, agent_model, analysis_completed_at, created_at'
        )
        .eq('object_id', objectId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (cancelled) return;

      if (!error && rows && rows.length > 0) {
        setData(rows[0] as FlightAnalysisResultDetail);
      } else {
        setData(null);
      }
      setLoading(false);
    };

    fetchResult();

    return () => {
      cancelled = true;
    };
  }, [objectId]);

  return { data, loading };
}
